import { jsx } from 'react/jsx-runtime';
import vtkVolume from '@kitware/vtk.js/Rendering/Core/Volume';
import vtkVolumeMapper from '@kitware/vtk.js/Rendering/Core/VolumeMapper';
import { forwardRef, useState, useCallback, useEffect, useMemo, useImperativeHandle } from 'react';
import { compareShallowObject } from '../utils/comparators.js';
import useBooleanAccumulator from '../utils/useBooleanAccumulator.js';
import useComparableEffect from '../utils/useComparableEffect.js';
import { useRendererContext, RepresentationContext, DownstreamContext } from './contexts.js';
import useColorAndOpacity from './modules/useColorAndOpacity.js';
import useColorTransferFunction from './modules/useColorTransferFunction.js';
import useDataEvents from './modules/useDataEvents.js';
import useDataRange from './modules/useDataRange.js';
import useMapper from './modules/useMapper.js';
import usePiecewiseFunction from './modules/usePiecewiseFunction.js';
import useProp from './modules/useProp.js';

const DefaultProps = {
    colorMapPreset: 'erdc_rainbow_bright',
    colorDataRange: 'auto',
};
var VolumeRepresentation = forwardRef(function VolumeRepresentation(props, fwdRef) {
    var _a, _b, _c, _d;
    const [modifiedRef, trackModified, resetModified] = useBooleanAccumulator();
    const [dataAvailable, setDataAvailable] = useState(false);
    // --- mapper --- //
    const getInternalMapper = useMapper(() => vtkVolumeMapper.newInstance(), props.mapper, trackModified);
    const { mapperInstance } = props;
    const getMapper = useCallback(() => {
        if (mapperInstance) {
            return mapperInstance;
        }
        return getInternalMapper();
    }, [mapperInstance, getInternalMapper]);
    const getInputData = useCallback(() => getMapper().getInputData(), [getMapper]);
    // --- data range --- //
    const getDataArray = useCallback(() => {
        var _a, _b;
        return (_b = (_a = getMapper()) === null || _a === void 0 ? void 0 : _a.getInputData()) === null || _b === void 0 ? void 0 : _b.getPointData().getScalars();
    }, [getMapper]);
    const { dataRange, updateDataRange } = useDataRange(getDataArray);
    const rangeFromProps = (_a = props.colorDataRange) !== null && _a !== void 0 ? _a : DefaultProps.colorDataRange;
    const colorDataRange = rangeFromProps === 'auto' ? dataRange : rangeFromProps;
    // --- LUT --- //
    const getLookupTable = useColorTransferFunction((_b = props.colorMapPreset) !== null && _b !== void 0 ? _b : DefaultProps.colorMapPreset, colorDataRange, trackModified);
    // --- PWF --- //
    const getPiecewiseFunction = usePiecewiseFunction(colorDataRange, trackModified);
    // --- actor --- //
    const actorProps = Object.assign(Object.assign({}, props.actor), { visibility: dataAvailable && ((_d = (_c = props.actor) === null || _c === void 0 ? void 0 : _c.visibility) !== null && _d !== void 0 ? _d : true) });
    const getActor = useProp({
        constructor: () => vtkVolume.newInstance(),
        id: props.id,
        props: actorProps,
        trackModified,
    });
    // --- color and opacity --- //
    const { colorTransferFunctions, scalarOpacityFunctions } = props;
    useEffect(() => {
        getActor().setMapper(getMapper());
    }, [getActor, getMapper]);
    useEffect(() => {
        getActor().getProperty().setRGBTransferFunction(0, getLookupTable());
        getActor().getProperty().setScalarOpacity(0, getPiecewiseFunction());
        getActor().getProperty().setInterpolationTypeToLinear();
    }, [getActor, getLookupTable, getPiecewiseFunction]);
    useColorAndOpacity(getActor, colorTransferFunctions, scalarOpacityFunctions, trackModified);
    // set actor property props
    const { property: propertyProps } = props;
    useComparableEffect(() => {
        if (!propertyProps)
            return;
        trackModified(getActor().getProperty().set(propertyProps));
    }, [propertyProps], ([cur], [prev]) => compareShallowObject(cur, prev));
    // --- events --- //
    const { dataChangedEvent, dataAvailableEvent } = useDataEvents(props);
    // trigger data available event
    useEffect(() => {
        if (dataAvailable) {
            dataAvailableEvent.current.dispatchEvent(getInputData());
        }
    }, [dataAvailable, dataAvailableEvent, getInputData]);
    // --- //
    const renderer = useRendererContext();
    useEffect(() => {
        if (modifiedRef.current) {
            renderer.requestRender();
            resetModified();
        }
    });
    const representation = useMemo(() => ({
        dataChanged: () => {
            updateDataRange();
            dataChangedEvent.current.dispatchEvent(getInputData());
            renderer.requestRender();
        },
        dataAvailable: (available = true) => {
            setDataAvailable(available);
            representation.dataChanged();
        },
        getActor,
        getMapper,
        onDataAvailable: (cb) => dataAvailableEvent.current.addEventListener(cb),
        onDataChanged: (cb) => dataChangedEvent.current.addEventListener(cb),
    }), [
        renderer,
        updateDataRange,
        getActor,
        getMapper,
        getInputData,
        dataAvailableEvent,
        dataChangedEvent,
    ]);
    const downstream = useMemo(() => ({
        setInputData: (...args) => getMapper().setInputData(...args),
        setInputConnection: (...args) => getMapper().setInputConnection(...args),
    }), [getMapper]);
    useImperativeHandle(fwdRef, () => representation);
    return (jsx(RepresentationContext.Provider, Object.assign({ value: representation }, { children: jsx(DownstreamContext.Provider, Object.assign({ value: downstream }, { children: props.children })) })));
});

export { VolumeRepresentation as default };
